import type { ChallengeApi } from '@mathflat/domain/@entities/Challenge/api'
import type { Entity as ConceptChipEntity } from '@mathflat/domain/@entities/ConceptChip/dto'
import type { StudentHistoryApi } from '@mathflat/domain/@entities/StudentHistory/api'
import { makeAutoObservable, runInAction } from 'mobx'

import challengeApi from '~/@common/api/challengeApi'
import { TOAST_STRING } from '~/@common/constants/strings'
import { toastService } from '~/@common/services'

class AssignedChallengeService {
  private _assignedChallenges: ChallengeApi.Response.AssignedChallenge[] = []
  private _achievements: StudentHistoryApi.성취도_등급_조회.ConceptChip[] = []

  constructor() {
    makeAutoObservable(this)
  }

  /**
   * 현재 하나의 값만 내려오지만, 추후 확장 가능성(학생이 n개의 반에서 챌린지를 할당받음)을 위해
   * 백엔드에서 리스트 형태로 보내주고 있어서 0번째 인덱스 값을 사용
   */
  private get _assignedChallenge() {
    return this._assignedChallenges[0]
  }

  /**
   * 현재 하나의 값만 내려오지만, 추후 확장 가능성(학생이 한 반에서 n개의 챌린지를 할당받음)을 위해
   * 백엔드에서 리스트 형태로 보내주고 있어서 chapters의 0번째 인덱스 값을 사용
   */
  private get _assignedConceptChip() {
    return this._assignedChallenge?.chapters[0]?.conceptChips ?? []
  }

  /**
   * 대단원, 중단원, 소단원의 정보는 리스트에서 내려오는 모든 conceptchip이 동일하므로 0번째 인덱스 값을 사용
   */
  get assignedConceptChipInfo() {
    return {
      bigChapterName: this._assignedConceptChip[0]?.bigChapterName ?? '',
      middleChapterName: this._assignedConceptChip[0]?.middleChapterName ?? '',
      littleChapterName: this._assignedConceptChip[0]?.littleChapterName ?? '',
      littleChapterId: this._assignedConceptChip[0]?.littleChapterId ?? '',
    }
  }

  get assignedConceptChipByType() {
    return {
      개념: this._assignedConceptChip.filter(
        (item: ConceptChipEntity.AchievementConceptChip) => item.conceptChipType === '개념',
      ),
      기본: this._assignedConceptChip.filter(
        (item: ConceptChipEntity.AchievementConceptChip) => item.conceptChipType === '기본',
      ),
      심화: this._assignedConceptChip.filter(
        (item: ConceptChipEntity.AchievementConceptChip) => item.conceptChipType === '심화',
      ),
    }
  }

  get isEmptyAssignedChallenge() {
    return this._assignedChallenges.length === 0
  }

  get achievements() {
    return this._achievements
  }

  get achievementMap() {
    return new Map(
      this.achievements.map((achievement) => [
        achievement.conceptChipId,
        achievement.achievementLevel,
      ]),
    )
  }

  fetchAssignedChallenges = async ({ studentId }: { studentId: string }) => {
    try {
      const assignedChallengesRes = await challengeApi.getAssignedChallenges()
      const assignedCurriculumKey = assignedChallengesRes.challenges[0]?.chapters[0]?.trieKey
      const achievementsRes = assignedCurriculumKey
        ? await challengeApi.getAchievements(studentId, {
            curriculumKey: assignedCurriculumKey,
          })
        : []
      runInAction(() => {
        this._assignedChallenges = assignedChallengesRes.challenges
        this._achievements = achievementsRes
      })
    } catch (error) {
      toastService.error(TOAST_STRING.error)
    }
  }
}

export default AssignedChallengeService
